import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ErrorState, LoadingState } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch } from "@/lib/hooks";
import { errorMessage } from "@/lib/api";

const gbp = (v) => `£${Number(v || 0).toFixed(v > 0 && v < 0.1 ? 3 : 2)}`;
const TASKS = { search_plan: "Search planning", evidence_extraction: "Evidence extraction", relevance_assessment: "Relevance assessment",
  partner_assessment: "Partner assessment", claim_verification: "Claim verification", web_discovery: "Web searches",
  product_profile_draft: "Draft with AI", product_draft_search: "Draft web searches",
  product_identify: "Datasheet product lookup", result_screening: "Result screening",
  customer_type_assessment: "Customer type assessment", customer_variants: "Search wording variants", places_discovery: "Google Maps business search", stage1_website: "Stage 1 website search", stage1_company_research: "Stage 1 company research", email_draft: "AI email drafts" };
const PERIODS = [["0", "All time"], ["30", "Last 30 days"], ["7", "Last 7 days"]];

function Breakdown({ item }) {
  if (!item) return null;
  return (
    <div className="mt-3 space-y-1 rounded-md border p-3 text-xs" data-testid="cost-item-detail">
      <div className="flex justify-between font-medium"><span>Total</span><span className="font-mono">{gbp(item.cost_gbp)}</span></div>
      <div className="flex justify-between text-muted-foreground"><span>{item.calls} calls{item.runs ? ` · ${item.runs} research runs` : ""}</span>{item.runs ? <span className="font-mono">{gbp(item.cost_gbp / item.runs)} / run</span> : null}</div>
      {item.tasks.map((t) => <div key={t.task} className="flex justify-between"><span className="text-muted-foreground">{TASKS[t.task] || t.task}</span><span className="font-mono">{gbp(t.cost_gbp)}</span></div>)}
    </div>
  );
}

function Picker({ label, items, testid }) {
  const [selected, setSelected] = useState("");
  if (!items.length) return <p className="text-xs text-muted-foreground">No {label.toLowerCase()} costs yet.</p>;
  return (
    <div>
      <Select value={selected} onValueChange={setSelected}>
        <SelectTrigger className="h-9 text-xs" data-testid={`${testid}-select`}><SelectValue placeholder={`Choose a ${label.toLowerCase().replace(/s$/, "")}…`} /></SelectTrigger>
        <SelectContent>
          {items.map((i) => <SelectItem key={i.id} value={i.id} data-testid={`${testid}-option-${i.id}`}>{i.name}{i.deleted ? " (deleted)" : ""} — {gbp(i.cost_gbp)}</SelectItem>)}
        </SelectContent>
      </Select>
      <Breakdown item={items.find((i) => i.id === selected)} />
    </div>
  );
}

function monthOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i += 1) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push([`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`, m.toLocaleDateString("en-GB", { month: "long", year: "numeric" })]);
  }
  return out;
}

function StatementDownload() {
  const { api } = useWorkspace();
  const months = monthOptions();
  const [month, setMonth] = useState(months[0][0]);
  const [busy, setBusy] = useState(null);
  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get("/billing/statement", { params: { month, format }, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `ajsource-costs-${month}.${format}`;
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement("a"), { href: url, download: name }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-2" data-testid="statement-download">
      <p className="eyebrow">Monthly statement</p>
      <div className="flex flex-wrap gap-2">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-9 min-w-0 flex-1 text-xs" data-testid="statement-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{months.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => download("pdf")} data-testid="statement-download-pdf">{busy === "pdf" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}PDF</Button>
        <Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => download("csv")} data-testid="statement-download-csv">{busy === "csv" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}CSV</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Usage taken from your research credit — a statement for your records, not a VAT invoice.</p>
    </div>
  );
}

export function CostCard() {
  const [days, setDays] = useState("0");
  const { data, loading, error, reload } = useFetch("/dashboard/costs", { days });
  return (
    <div className="panel space-y-4 p-5" data-testid="usage-costs-card">
      <div className="flex items-center justify-between gap-2">
        <p className="eyebrow">Research credit used</p>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-7 w-32 text-xs" data-testid="cost-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && (
        <>
          <div>
            <p className="font-display text-3xl font-extrabold" data-testid="cost-total">{gbp(data.total_gbp)}</p>
            <p className="mt-1 text-xs text-muted-foreground">Credit used by each search and AI step, as deducted from your balance.</p>
          </div>
          <div className="space-y-1.5" data-testid="cost-providers">
            <p className="eyebrow">By provider</p>
            {!data.providers.length && <p className="text-xs text-muted-foreground">No calls logged yet.</p>}
            {data.providers.map((p) => (
              <div key={p.provider} className="flex items-center justify-between text-sm" data-testid={`cost-provider-${p.provider}`}>
                <span>{p.label}<span className="ml-1.5 text-xs text-muted-foreground">{p.calls} calls</span></span><span className="font-mono">{gbp(p.cost_gbp)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2"><p className="eyebrow">By research campaign</p><Picker label="Campaigns" items={data.campaigns} testid="cost-campaign" /></div>
          <div className="space-y-2"><p className="eyebrow">By product profile</p><Picker label="Product profiles" items={data.products} testid="cost-product" /></div>
          {data.unattributed.calls > 0 && <p className="text-xs text-muted-foreground" data-testid="cost-unattributed">{data.unattributed.label}: <span className="font-mono">{gbp(data.unattributed.cost_gbp)}</span></p>}
          <StatementDownload />
        </>
      )}
      <Link to="/integrations" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">Integration status <ArrowUpRight className="h-3 w-3" /></Link>
    </div>
  );
}
